<!-- p-dialog is only the mount (backdrop + focus-trap + body-portal); the shared
     @u2bUi/dialog chrome renders the panel, matching the wizard and its sub-dialogs. -->
<p-dialog
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    [showHeader]="false"
    styleClass="booking-v2-dialog-panel"
    [style]="{ width: '46rem', maxWidth: '95vw' }"
    appendTo="body"
    [visible]="visible()"
    (visibleChange)="visible.set($event)">

    <u2b-dialog class="rounded-[inherit] w-full max-h-[88vh]">

        <div u2bDialogHeader class="contents">
            <h1 class="text-lg font-semibold m-0 text-p-primary-contrast">
                {{ 'bookingV2.contract.title' | translate:{ name: person().displayName } }}
            </h1>
            <u2b-icon-button
                variant="on-primary"
                icon="close"
                [ariaLabel]="('common.closeDialogAria' | translate)"
                (clicked)="close()" />
        </div>

        <div class="flex flex-col gap-5">

            <!-- ── Vorlage ── -->
            <div class="flex flex-col gap-1.5">
                <label for="contract-template" class="text-sm font-medium text-p-text">
                    {{ 'bookingV2.contract.templateLabel' | translate }}
                    <span class="text-red-500" aria-hidden="true">*</span>
                </label>
                <p-select
                    inputId="contract-template"
                    styleClass="w-full"
                    appendTo="body"
                    [filter]="true"
                    filterBy="title"
                    optionLabel="title"
                    [group]="true"
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                    [options]="groupedContracts()"
                    [ngModel]="selectedContract()"
                    (ngModelChange)="selectedContract.set($event)"
                    [placeholder]="('bookingV2.contract.templatePlaceholder' | translate)">
                    <ng-template #dropdownicon><span class="material-icons">expand_more</span></ng-template>
                    <ng-template #filtericon><span class="material-icons">search</span></ng-template>
                </p-select>
                @if (contractTemplateMissing()) {
                    <u2b-indicator-card accent="amber" icon="error_outline">
                        <p class="text-sm text-p-text m-0">{{ 'bookingV2.contract.templateFileMissing' | translate }}</p>
                    </u2b-indicator-card>
                }
            </div>

            <!-- ── Fehlende Daten ── -->
            @if (hasMissingFields()) {
                <u2b-indicator-card accent="amber" icon="warning">
                    <p class="text-sm font-semibold text-p-text m-0">{{ 'bookingV2.contract.missing.title' | translate }}</p>
                    <p class="text-sm text-p-text m-0 mt-0.5">{{ 'bookingV2.contract.missing.intro' | translate }}</p>
                    @for (group of missingGroupOrder; track group) {
                        @if (missingGroup(group).length > 0) {
                            <p class="text-sm font-medium text-p-text m-0 mt-1.5">{{ 'bookingV2.contract.missing.' + group | translate }}</p>
                            <ul class="text-sm text-p-text m-0 mt-0.5 pl-5 list-disc">
                                @for (item of missingGroup(group); track item) { <li>{{ item }}</li> }
                            </ul>
                        }
                    }
                </u2b-indicator-card>
            }

            <!-- ── Zeitraum ── -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="flex flex-col gap-1.5">
                    <label for="contract-from" class="text-sm font-medium text-p-text">{{ 'bookingV2.contract.fromLabel' | translate }}</label>
                    <p-datepicker inputId="contract-from" u2bDateMask dateFormat="dd.mm.yy" [showIcon]="true" appendTo="body"
                        styleClass="w-full" [ngModel]="from()" (ngModelChange)="from.set($event)">
                        <ng-template #triggericon><span class="material-icons">calendar_today</span></ng-template>
                        <ng-template #previousicon><span class="material-icons">chevron_left</span></ng-template>
                        <ng-template #nexticon><span class="material-icons">chevron_right</span></ng-template>
                    </p-datepicker>
                </div>
                <div class="flex flex-col gap-1.5">
                    <label for="contract-to" class="text-sm font-medium text-p-text">{{ 'bookingV2.contract.toLabel' | translate }}</label>
                    <p-datepicker inputId="contract-to" u2bDateMask dateFormat="dd.mm.yy" [showIcon]="true" appendTo="body"
                        styleClass="w-full" [minDate]="from() ?? undefined" [ngModel]="to()" (ngModelChange)="to.set($event)">
                        <ng-template #triggericon><span class="material-icons">calendar_today</span></ng-template>
                        <ng-template #previousicon><span class="material-icons">chevron_left</span></ng-template>
                        <ng-template #nexticon><span class="material-icons">chevron_right</span></ng-template>
                    </p-datepicker>
                </div>
            </div>

            <!-- ── Boot (nur wenn die Vorlage BOOT_-Platzhalter hat) ── -->
            @if (hasBoatCodes()) {
                <div class="flex flex-col gap-1.5">
                    <span class="text-sm font-medium text-p-text">{{ 'bookingV2.contract.boatLabel' | translate }}</span>
                    @if (boatOption(); as b) {
                        <div class="flex items-center gap-2 rounded-lg border border-p-border bg-p-content-bg px-3 py-2">
                            <span class="material-icons text-p-text-muted" aria-hidden="true">directions_boat</span>
                            <span class="font-medium text-p-text">{{ b.name || ('bookingV2.berth.summary.unnamedBoat' | translate) }}</span>
                        </div>
                    } @else {
                        <u2b-indicator-card accent="amber" icon="warning">
                            <p class="text-sm text-p-text m-0">{{ 'bookingV2.contract.noBoatWarning' | translate }}</p>
                        </u2b-indicator-card>
                    }
                </div>
            }

            <!-- ── Liegeplatz (nur wenn die Vorlage LP_-Platzhalter hat) ── -->
            @if (hasBerthCodes()) {
                <div class="flex flex-col gap-1.5">
                    <label for="contract-berth" class="text-sm font-medium text-p-text">
                        {{ 'bookingV2.contract.berthLabel' | translate }}
                        <span class="text-red-500" aria-hidden="true">*</span>
                    </label>
                    @if (berths().length > 0) {
                        <p-select inputId="contract-berth" styleClass="w-full" appendTo="body" optionValue="id" optionLabel="handle"
                            [options]="berthOptions()" [ngModel]="selectedBerthId()" (ngModelChange)="onBerthChange($event)"
                            [placeholder]="('bookingV2.contract.berthPlaceholder' | translate)">
                            <ng-template #dropdownicon><span class="material-icons">expand_more</span></ng-template>
                        </p-select>
                    } @else {
                        <u2b-indicator-card accent="amber" icon="warning">
                            <p class="text-sm text-p-text m-0">{{ 'bookingV2.contract.noBerthWarning' | translate }}</p>
                        </u2b-indicator-card>
                    }
                </div>
            }

            <!-- ── Miteigner (VMI_) ── -->
            @if (hasCoOwnerCodes()) {
                <div class="rounded-lg border border-p-border p-5 flex flex-col gap-4">
                    <div class="flex items-center justify-between gap-3">
                        <span class="text-sm font-semibold text-p-text">{{ 'bookingV2.contract.coOwner.title' | translate }}</span>
                        <p-toggleswitch [ngModel]="captureCoOwner()" (ngModelChange)="captureCoOwner.set($event)" />
                    </div>
                    @if (captureCoOwner()) {
                        <div class="flex gap-1.5">
                            @for (n of [0,1,2,3]; track n) {
                                <button type="button"
                                    class="px-3 py-1 rounded-md text-sm border"
                                    [class.bg-p-primary]="activeCoOwner() === n"
                                    [class.text-p-primary-contrast]="activeCoOwner() === n"
                                    [class.border-p-primary]="activeCoOwner() === n"
                                    [class.border-p-border]="activeCoOwner() !== n"
                                    [class.text-p-text]="activeCoOwner() !== n"
                                    (click)="activeCoOwner.set(n)">
                                    {{ 'bookingV2.contract.coOwner.tab' | translate:{ n: n + 1 } }}
                                </button>
                            }
                        </div>
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div class="flex flex-col gap-1 sm:col-span-2">
                                <label class="text-sm font-medium text-p-text">{{ 'bookingV2.contract.coOwner.fullName' | translate }}</label>
                                <input pInputText type="text" class="w-full"
                                    [ngModel]="coOwners()[activeCoOwner()].name"
                                    (ngModelChange)="updateCoOwner(activeCoOwner(), 'name', $event)" />
                            </div>
                            <div class="flex flex-col gap-1 sm:col-span-2">
                                <label class="text-sm font-medium text-p-text">{{ 'bookingV2.contract.coOwner.street' | translate }}</label>
                                <input pInputText type="text" class="w-full"
                                    [ngModel]="coOwners()[activeCoOwner()].str"
                                    (ngModelChange)="updateCoOwner(activeCoOwner(), 'str', $event)" />
                            </div>
                            <div class="flex flex-col gap-1">
                                <label class="text-sm font-medium text-p-text">{{ 'bookingV2.contract.coOwner.postcode' | translate }}</label>
                                <input pInputText type="text" class="w-full"
                                    [ngModel]="coOwners()[activeCoOwner()].plz"
                                    (ngModelChange)="updateCoOwner(activeCoOwner(), 'plz', $event)" />
                            </div>
                            <div class="flex flex-col gap-1">
                                <label class="text-sm font-medium text-p-text">{{ 'bookingV2.contract.coOwner.city' | translate }}</label>
                                <input pInputText type="text" class="w-full"
                                    [ngModel]="coOwners()[activeCoOwner()].ort"
                                    (ngModelChange)="updateCoOwner(activeCoOwner(), 'ort', $event)" />
                            </div>
                        </div>
                    }
                </div>
            }

            <!-- ── Baukostenzuschuss (BKZ_) ── -->
            @if (hasBkzCodes()) {
                <div class="rounded-lg border border-p-border p-5 flex flex-col gap-4">
                    <div class="flex items-center justify-between gap-3">
                        <span class="text-sm font-semibold text-p-text">{{ 'bookingV2.contract.bkz.title' | translate }}</span>
                        <p-toggleswitch [ngModel]="captureBkz()" (ngModelChange)="captureBkz.set($event)" />
                    </div>
                    @if (captureBkz()) {
                        <div class="flex flex-col gap-1">
                            <label class="text-sm font-medium text-p-text">{{ 'bookingV2.contract.bkz.productPrefill' | translate }}</label>
                            <p-select styleClass="w-full" appendTo="body" [filter]="true" filterBy="name" optionLabel="name"
                                [options]="products()" [ngModel]="bkzProduct()" (ngModelChange)="onBkzProductChange($event)"
                                [placeholder]="('bookingV2.contract.bkz.productPlaceholder' | translate)" [showClear]="true">
                                <ng-template #dropdownicon><span class="material-icons">expand_more</span></ng-template>
                                <ng-template #filtericon><span class="material-icons">search</span></ng-template>
                            </p-select>
                        </div>
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div class="flex flex-col gap-1">
                                <label class="text-sm font-medium text-p-text">{{ 'bookingV2.contract.bkz.amountNet' | translate }}</label>
                                <p-inputnumber styleClass="w-full" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="2"
                                    [ngModel]="bkz().BKZ_BETRAG_NETTO" (ngModelChange)="setBkz('BKZ_BETRAG_NETTO', $event)" />
                            </div>
                            <div class="flex flex-col gap-1">
                                <label class="text-sm font-medium text-p-text">{{ 'bookingV2.contract.bkz.vat' | translate }}</label>
                                <p-inputnumber styleClass="w-full" suffix=" %"
                                    [ngModel]="bkz().BKZ_MWST" (ngModelChange)="setBkz('BKZ_MWST', $event)" />
                            </div>
                            <div class="flex flex-col gap-1">
                                <label class="text-sm font-medium text-p-text">{{ 'bookingV2.contract.bkz.totalNet' | translate }}</label>
                                <p-inputnumber styleClass="w-full" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="2"
                                    [ngModel]="bkz().BKZ_BETRAG_GESAMT_NETTO" (ngModelChange)="setBkz('BKZ_BETRAG_GESAMT_NETTO', $event)" />
                            </div>
                            <div class="flex flex-col gap-1">
                                <label class="text-sm font-medium text-p-text">{{ 'bookingV2.contract.bkz.totalGross' | translate }}</label>
                                <p-inputnumber styleClass="w-full" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="2"
                                    [ngModel]="bkz().BKZ_BETRAG_GESAMT_BRUTTO" (ngModelChange)="setBkz('BKZ_BETRAG_GESAMT_BRUTTO', $event)" />
                            </div>
                            <div class="flex flex-col gap-1 sm:col-span-2">
                                <label class="text-sm font-medium text-p-text">{{ 'bookingV2.contract.bkz.dueDate' | translate }}</label>
                                <p-datepicker u2bDateMask dateFormat="dd.mm.yy" [showIcon]="true" appendTo="body" styleClass="w-full"
                                    [ngModel]="bkz().BKZ_BETRAG_FAELLIG_ZUM" (ngModelChange)="setBkz('BKZ_BETRAG_FAELLIG_ZUM', $event)">
                                    <ng-template #triggericon><span class="material-icons">calendar_today</span></ng-template>
                                </p-datepicker>
                            </div>
                            <div class="flex flex-col gap-1">
                                <label class="text-sm font-medium text-p-text">{{ 'bookingV2.contract.bkz.installment1' | translate }}</label>
                                <p-inputnumber styleClass="w-full" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="2"
                                    [ngModel]="bkz().BKZ_TZ_1_BETRAG" (ngModelChange)="setBkz('BKZ_TZ_1_BETRAG', $event)" />
                            </div>
                            <div class="flex flex-col gap-1">
                                <label class="text-sm font-medium text-p-text">{{ 'bookingV2.contract.bkz.installment1Due' | translate }}</label>
                                <p-datepicker u2bDateMask dateFormat="dd.mm.yy" [showIcon]="true" appendTo="body" styleClass="w-full"
                                    [ngModel]="bkz().BKZ_TZ_1_FAELLIG_ZUM" (ngModelChange)="setBkz('BKZ_TZ_1_FAELLIG_ZUM', $event)">
                                    <ng-template #triggericon><span class="material-icons">calendar_today</span></ng-template>
                                </p-datepicker>
                            </div>
                            <div class="flex flex-col gap-1">
                                <label class="text-sm font-medium text-p-text">{{ 'bookingV2.contract.bkz.installment2' | translate }}</label>
                                <p-inputnumber styleClass="w-full" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="2"
                                    [ngModel]="bkz().BKZ_TZ_2_BETRAG" (ngModelChange)="setBkz('BKZ_TZ_2_BETRAG', $event)" />
                            </div>
                            <div class="flex flex-col gap-1">
                                <label class="text-sm font-medium text-p-text">{{ 'bookingV2.contract.bkz.installment2Due' | translate }}</label>
                                <p-datepicker u2bDateMask dateFormat="dd.mm.yy" [showIcon]="true" appendTo="body" styleClass="w-full"
                                    [ngModel]="bkz().BKZ_TZ_2_FAELLIG_ZUM" (ngModelChange)="setBkz('BKZ_TZ_2_FAELLIG_ZUM', $event)">
                                    <ng-template #triggericon><span class="material-icons">calendar_today</span></ng-template>
                                </p-datepicker>
                            </div>
                        </div>
                    }
                </div>
            }

            <!-- ── Produkte (PRODUKT) ── -->
            @if (hasProductCodes()) {
                <div class="rounded-lg border border-p-border p-5 flex flex-col gap-4">
                    <div class="flex items-center justify-between gap-3">
                        <span class="text-sm font-semibold text-p-text">{{ 'bookingV2.contract.products.title' | translate }}</span>
                        <p-toggleswitch [ngModel]="captureProducts()" (ngModelChange)="captureProducts.set($event)" />
                    </div>
                    @if (captureProducts()) {
                        @for (row of productRows(); track $index) {
                            <div class="flex items-end gap-2">
                                <div class="flex-1 flex flex-col gap-1 min-w-0">
                                    <label class="text-sm font-medium text-p-text">{{ 'bookingV2.contract.products.product' | translate }}</label>
                                    <p-select styleClass="w-full" appendTo="body" [filter]="true" filterBy="name" optionLabel="name"
                                        [options]="products()" [ngModel]="row.product" (ngModelChange)="setProductRow($index, { product: $event })"
                                        [placeholder]="('bookingV2.contract.products.productPlaceholder' | translate)">
                                        <ng-template #dropdownicon><span class="material-icons">expand_more</span></ng-template>
                                        <ng-template #filtericon><span class="material-icons">search</span></ng-template>
                                    </p-select>
                                </div>
                                <div class="w-32 flex flex-col gap-1">
                                    <label class="text-sm font-medium text-p-text">{{ 'bookingV2.contract.products.quantity' | translate }}</label>
                                    <p-inputnumber styleClass="w-full" [min]="0" [ngModel]="row.quantity"
                                        (ngModelChange)="setProductRow($index, { quantity: $event })" />
                                </div>
                                <u2b-icon-button icon="close" [ariaLabel]="('common.remove' | translate)" (clicked)="removeProductRow($index)" />
                            </div>
                        }
                        <div>
                            <p-button type="button" severity="secondary" [text]="true" (onClick)="addProductRow()">
                                <span pButtonIcon class="material-icons" style="font-size: 1.125rem">add</span>
                                <span pButtonLabel>{{ 'bookingV2.contract.products.add' | translate }}</span>
                            </p-button>
                        </div>
                    }
                </div>
            }

            <!-- ── Unbekannte Platzhalter ── -->
            @if (unknownShortCodes().length > 0) {
                <div class="rounded-lg border border-p-border p-5 flex flex-col gap-4">
                    <span class="text-sm font-semibold text-p-text">{{ 'bookingV2.contract.unknown.title' | translate }}</span>
                    @for (code of unknownShortCodes(); track code) {
                        <div class="flex flex-col gap-1">
                            <label class="text-sm font-medium text-p-text">{{ code }}</label>
                            <input pInputText type="text" class="w-full"
                                [ngModel]="unknownValues()[code] ?? ''" (ngModelChange)="setUnknown(code, $event)" />
                        </div>
                    }
                </div>
            }

            <!-- ── Ausgabe ── -->
            <div class="rounded-lg border border-p-border p-5 flex flex-col gap-4">
                <span class="text-sm font-semibold text-p-text">{{ 'bookingV2.contract.output.title' | translate }}</span>

                @if (personMail(); as mail) {
                    <div class="flex items-center gap-2">
                        <p-checkbox inputId="contract-sendmail" [binary]="true" [disabled]="contractTemplateMissing()"
                            [ngModel]="sendMail()" (ngModelChange)="sendMail.set($event)" />
                        <label for="contract-sendmail" class="text-sm text-p-text cursor-pointer select-none m-0">
                            {{ 'bookingV2.contract.output.sendMail' | translate:{ mail } }}
                        </label>
                    </div>
                }

                <div class="flex items-center gap-2">
                    <p-checkbox inputId="contract-download" [binary]="true" [disabled]="contractTemplateMissing()"
                        [ngModel]="downloadPdf()" (ngModelChange)="downloadPdf.set($event)" />
                    <label for="contract-download" class="text-sm text-p-text cursor-pointer select-none m-0">
                        {{ 'bookingV2.contract.downloadPdf' | translate }}
                    </label>
                </div>

                @if (showFileTypeRadio()) {
                    <div class="flex flex-col gap-2 pt-1">
                        <span class="text-sm font-medium text-p-text">{{ 'bookingV2.contract.output.fileType' | translate }}</span>
                        <div class="flex items-center gap-4">
                            <div class="flex items-center gap-2">
                                <p-radiobutton inputId="ft-mail" name="contractFileType" [value]="ContractFileType.MAIL"
                                    [disabled]="!selectedContract()?.templateMailFile"
                                    [ngModel]="contractFileType()" (ngModelChange)="contractFileType.set($event)" />
                                <label for="ft-mail" class="text-sm text-p-text cursor-pointer m-0">{{ 'bookingV2.contract.output.mailTemplate' | translate }}</label>
                            </div>
                            <div class="flex items-center gap-2">
                                <p-radiobutton inputId="ft-print" name="contractFileType" [value]="ContractFileType.PRINT"
                                    [disabled]="!selectedContract()?.templatePrintFile"
                                    [ngModel]="contractFileType()" (ngModelChange)="contractFileType.set($event)" />
                                <label for="ft-print" class="text-sm text-p-text cursor-pointer m-0">{{ 'bookingV2.contract.output.printTemplate' | translate }}</label>
                            </div>
                        </div>
                    </div>
                }
            </div>

            <!-- ── Signatur ── -->
            <div class="rounded-lg border border-p-border p-5 flex flex-col gap-4">
                <div class="flex items-center justify-between gap-3">
                    <span class="text-sm font-semibold text-p-text">{{ 'bookingV2.contract.signature.title' | translate }}</span>
                    <p-toggleswitch [ngModel]="signEnabled()" (ngModelChange)="signEnabled.set($event)" />
                </div>
                @if (signEnabled()) {
                    <div class="flex items-center gap-4">
                        <div class="flex items-center gap-2">
                            <p-radiobutton inputId="sig-draw" name="sigMode" value="draw"
                                [ngModel]="signMode()" (ngModelChange)="signMode.set($event)" />
                            <label for="sig-draw" class="text-sm text-p-text cursor-pointer m-0">{{ 'bookingV2.contract.signature.draw' | translate }}</label>
                        </div>
                        <div class="flex items-center gap-2">
                            <p-radiobutton inputId="sig-text" name="sigMode" value="text"
                                [ngModel]="signMode()" (ngModelChange)="signMode.set($event)" />
                            <label for="sig-text" class="text-sm text-p-text cursor-pointer m-0">{{ 'bookingV2.contract.signature.text' | translate }}</label>
                        </div>
                    </div>
                    @if (signMode() === 'draw') {
                        <div class="flex flex-col gap-2">
                            <div class="border border-p-border rounded-lg bg-white overflow-hidden self-start">
                                <signature-pad [options]="signaturePadOptions"></signature-pad>
                            </div>
                            <div>
                                <p-button type="button" severity="secondary" [text]="true" [label]="'bookingV2.contract.signature.clear' | translate" (onClick)="clearSignature()" />
                            </div>
                        </div>
                    } @else {
                        <input pInputText type="text" class="w-full"
                            [placeholder]="('bookingV2.contract.signature.textPlaceholder' | translate)"
                            [ngModel]="signatureText()" (ngModelChange)="signatureText.set($event)" />
                    }
                }
            </div>

        </div>

        <div u2bDialogFooter class="contents">
            <p-button type="button" severity="secondary" [text]="true" [label]="'common.cancel' | translate" (onClick)="close()" />
            <div class="flex items-center gap-2">
                <p-button type="button" severity="secondary" [outlined]="true" [disabled]="!canSubmit()"
                    [label]="'bookingV2.contract.preview' | translate" (onClick)="submit(true)" />
                <p-button type="button" [disabled]="!canSubmit()"
                    [label]="'bookingV2.contract.create' | translate" (onClick)="submit(false)" />
            </div>
        </div>
    </u2b-dialog>
</p-dialog>
